import React, { useState } from "react"
import { useStaticQuery, graphql } from "gatsby"

import SectionDivider from "../shared/section-divider"
import useVideoProgressManager from "../shared/hooks/useVideoProgressManager"

import { css } from "styled-components"
import {
  Grid,
  Block,
  Spacer,
} from "../../design-system/atoms/layout-primitives"
import VideoIconProgressBar from "../../design-system/molecules/video-icon-progress-bar"
import ReactPlayer from "react-player"
import VisibilitySensor from "react-visibility-sensor"

import { SectionTitle, DescriptionText } from "./videos.style"
import { FadeIn } from "../../design-system/styles/animations.styles"

export default function Component() {
  const query = useStaticQuery(graphql`
    query {
      autoPathIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/auto paths.svg" }
      ) {
        publicURL
      }
      autoWallsIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/auto walls.svg" }
      ) {
        publicURL
      }
      colourAttrsIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/colour.svg" }
      ) {
        publicURL
      }
      heightAttrsIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/height.svg" }
      ) {
        publicURL
      }
      polyHighlightIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/highlight polygons.svg" }
      ) {
        publicURL
      }
      finalRenderIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/render.svg" }
      ) {
        publicURL
      }
      splitMergeIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/split merge.svg" }
      ) {
        publicURL
      }
      polyTraceIcon: file(
        relativePath: { eq: "0_HOMEPAGE/control icons/tracing.svg" }
      ) {
        publicURL
      }
    }
  `)

  const VideoElementDetails = {
    polyTrace: {
      title: "Tracing Geometry",
      start: 0,
      end: 15,
      
    },
    autoPath: {
      title: "Automated Pathing",
      start: 15,
      end: 32,
      
    },
    heightAttrs: {
      title: "Height Attributes",
      start: 32,
      end: 61,
      
    },
    colourAttrs: {
      title: "Colour Attributes",
      start: 61,
      end: 79,
      
    },
    autoWalls: {
      title: "Automated Walls",
      start: 79,
      end: 100,
      
    },
    polyHighlight: {
      title: "Highlight Polygons",
      start: 100,
      end: 105,
      
    },
    finalRender: {
      title: "Final Render",
      start: 105,
      end: 117,
      
    },
    splitMerge: {
      title: "Split & Merge",
      start: 117,
      end: 140,
      
    },
  }

  const [isPlayerVisible, setPlayerVisible] = useState(false)

  const { videoPlayerRef, videoProgress, setVideoProgress, seekToTimestamp } =
    useVideoProgressManager()

  return (
    <>
      <SectionDivider />
      <Block variants={[{ backgroundColor: "var(--tricorn-black)" }]}>
        <Spacer height={{ sm: "64px", md: "64px", lg: "96px" }} />
        <Grid.Animated animation={FadeIn}>
          <SectionTitle>
            <span>Spend less time mapping </span> and more time running your
            business
          </SectionTitle>
          <DescriptionText>
            Mappedin’s focus on automation and simple editor tools allows you to
            maintain maps frequently. The result? Accurate maps across your
            organization.
          </DescriptionText>
          <Spacer height={{ sm: "32px", md: "48px", lg: "64px" }} />
          <Block
            variants={[
              css`
                width: 100vw;
                height: calc(56.25vw);
                grid-column: 1 / -1;
                align-self: center;
                justify-self: center;
              `,
              css`
                grid-column: 2 / -2;
                width: 100%;
                height: calc(0.777 * 56.25vw);
              `,
              css`
                grid-column: 3 / -3;
                height: calc(0.632 * 56.25vw);
                max-height: 512px;
              `,
            ]}
          >
            <VisibilitySensor
              minTopValue={200}
              partialVisibility={true}
              onChange={setPlayerVisible}
            >
              <ReactPlayer
                url="https://vimeo.com/427469580/d30c41a78e"
                playing={isPlayerVisible}
                loop={true}
                volume={0}
                muted={true}
                controls={false}
                onProgress={(data) => setVideoProgress(data)}
                ref={videoPlayerRef}
                playsinline={true}
                width={"100%"}
                height={"100%"}
              />
            </VisibilitySensor>
          </Block>

          <Spacer height={{ sm: "48px", md: "48px", lg: "64px" }} />

          <Block
            variants={[
              css`
                && {
                  display: grid;
                }
                grid-column: 2 / -2;
                grid-template-columns: 1fr;
                grid-column-gap: 114px;
                grid-row-gap: 32px;
              `,
              css`
                grid-column: 2 / -2;
                grid-template-columns: 1fr 1fr;
              `,
              css`
                grid-column: 4 / -4;
              `,
            ]}
          >
            <VideoIconProgressBar
              details={VideoElementDetails.polyTrace}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.polyTraceIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.autoPath}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.autoPathIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.heightAttrs}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.heightAttrsIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.colourAttrs}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.colourAttrsIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.autoWalls}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.autoWallsIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.polyHighlight}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.polyHighlightIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.finalRender}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.finalRenderIcon.publicURL}></img>}
            />
            <VideoIconProgressBar
              details={VideoElementDetails.splitMerge}
              videoProgress={videoProgress}
              seekMethod={seekToTimestamp}
              icon={<img src={query.splitMergeIcon.publicURL}></img>}
            />
          </Block>
        </Grid.Animated>
        <Spacer height={{ sm: "64px", md: "64px", lg: "96px" }} />
      </Block>
      <SectionDivider flip />
    </>
  )
}
